import React from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

function BusinessLogoPlaceholder({ name = "", className = "h-36 w-auto" }) {
  const initial = name?.trim()?.charAt(0)?.toUpperCase() || "H";
  return (
    <div className="flex flex-col items-center text-center shrink-0">
      <div className="flex h-32 w-32 items-center justify-center rounded-xl bg-red-50 border-2 border-red-600 text-red-600 font-bold text-4xl shadow-xs">
        {initial}
      </div>
    </div>
  );
}

export default function ChaiReportHeader({
  reportName,
  businessInfo: customBusinessInfo = null,
  subtitle = "",
  showChaiLogo: customShowChaiLogo = true,
  showBusinessLogo: customShowBusinessLogo = true,
  settingsOverride = false,
  className = "",
}) {
  const { t } = useTranslation();
  const auth = useSelector((state) => state?.auth);
  const businessInfo = customBusinessInfo || auth?.buisnessInfo || {};
  const settings = auth?.medicalReportSettings || {};

  const showBusinessLogo = settingsOverride
    ? customShowBusinessLogo
    : settings?.show_business_logo ?? customShowBusinessLogo;
  const showChaiLogo = settingsOverride
    ? customShowChaiLogo
    : settings?.show_chai_logo ?? customShowChaiLogo;

  const companyNameAr = businessInfo?.name_ar || "";
  const companyNameEn = businessInfo?.name_en || "";
  const activeTitle = reportName || t("LABORATORY REPORT");

  const logoSrc = (() => {
    const raw = businessInfo?.logo || businessInfo?.logo_url;
    if (!raw) return "";
    if (
      typeof raw === "string" &&
      (raw.startsWith("data:") || raw.startsWith("http"))
    ) {
      return raw;
    }
    return `data:image/png;base64,${raw}`;
  })();

  const licenseNo = businessInfo?.license_no || businessInfo?.licenseNo || "";
  const crNo = businessInfo?.cr_no || businessInfo?.crno || "";
  const phone = businessInfo?.contact_info || businessInfo?.phone || "";
  const email = businessInfo?.email || "";

  return (
    <header className={`chai-report-header mb-2 px-4 ${className}`}>
      <style>{`
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .chai-header-badge-green {
          background-color: #278644 !important;
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          white-space: nowrap !important;
          flex-wrap: nowrap !important;
        }
        .chai-header-badge-green * {
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
        }
        .chai-header-title-badge {
          background-color: #D6515E !important;
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .chai-header-title-badge * {
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
        }
        .chai-header-divider-line {
          background-color: #D6515E !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      `}</style>
      <div className="flex items-stretch gap-0">
        {showBusinessLogo && (
          <div className="flex flex-col justify-between shrink-0">
            <div className="flex-1 flex items-end justify-center pr-3 pb-1">
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt={companyNameEn || companyNameAr || "Logo"}
                  className="w-auto max-w-[190px] object-contain object-bottom"
                  style={{ maxHeight: "115px" }}
                />
              ) : (
                <BusinessLogoPlaceholder
                  name={companyNameEn || companyNameAr}
                  className="h-32 w-auto"
                />
              )}
            </div>
          </div>
        )}
        <div className="flex-1 flex flex-col justify-between min-w-0">
          <div
            className={`flex items-center justify-between gap-1 pt-1 ${
              showBusinessLogo ? "pl-2" : ""
            }`}
          >
            {showChaiLogo && (
              <div className="shrink-0 flex items-center">
                <img
                  src="/images/cbahi-logo.jpg"
                  alt="CBAHI"
                  className="h-auto w-auto object-contain"
                  style={{ maxHeight: "80px", maxWidth: "125px" }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            )}

            <div className="flex flex-col items-center text-center gap-3 px-1 flex-1 min-w-0">
              {companyNameAr && (
                <h1
                  className="text-2xl md:text-3xl lg:text-[38px] font-black !text-[#278644] tracking-normal leading-tight"
                  dir="rtl"
                >
                  {companyNameAr}
                </h1>
              )}
              {companyNameEn && (
                <h2 className="text-base md:text-lg lg:text-[24px] font-bold !text-[#D6515E] tracking-wider uppercase leading-tight mt-0.5">
                  {companyNameEn}
                </h2>
              )}
            </div>
            <div className="shrink-0 flex items-center select-none pr-1 rtl:pr-0 rtl:pl-1" dir="ltr">
              <div className="relative flex items-center py-1">
                <div className="absolute inset-0 flex items-center justify-center opacity-20 pointer-events-none">
                  <svg
                    viewBox="0 0 100 100"
                    className="w-14 h-14 text-[#278644]"
                    fill="currentColor"
                  >
                    <path d="M 50,14 L 54,40 L 82,26 L 64,50 L 82,74 L 54,60 L 50,86 L 46,60 L 18,74 L 36,50 L 18,26 L 46,40 Z" />
                  </svg>
                </div>
                <div className="flex flex-col text-[#278644] font-bold text-[12px] leading-[15px] tracking-tight z-10">
                  <span className="pl-0">Pledged</span>
                  <span className="pl-2">to create</span>
                  <span className="pl-3.5">a healthy</span>
                  <span className="pl-0.5">World...</span>
                </div>
                <div className="shrink-0 -ml-8 flex items-center z-0">
                  <svg
                    width="44"
                    height="84"
                    viewBox="0 0 56 90"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="overflow-visible"
                  >
                    <path
                      d="M 14,4 C 36,0 54,18 54,45 C 54,72 36,90 14,86 C 32,83 48,68 48,45 C 48,22 32,7 14,4 Z"
                      fill="#D6515E"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {(licenseNo || crNo || phone || email) && (
            <div className="flex items-center justify-center w-full">
              <div
                className="chai-header-badge-green inline-flex font-semibold flex-nowrap whitespace-nowrap items-center justify-center gap-x-1.5 rounded-full bg-[#278644] !text-white px-2.5 py-1 text-[9px] tracking-tight"
                dir="rtl"
                style={{
                  backgroundColor: "#278644",
                  color: "#ffffff",
                  WebkitTextFillColor: "#ffffff",
                  WebkitPrintColorAdjust: "exact",
                  printColorAdjust: "exact",
                }}
              >
                {licenseNo && (
                  <span className="inline-flex items-center gap-0.5">
                    <span>رقم ترخيص:</span>
                    <span dir="ltr">{licenseNo}</span>
                  </span>
                )}
                {licenseNo && (crNo || phone || email) && (
                  <span className="opacity-60 font-normal">•</span>
                )}
                {crNo && (
                  <span className="inline-flex items-center gap-0.5">
                    <span>السجل التجاري:</span>
                    <span dir="ltr">{crNo}</span>
                  </span>
                )}
                {crNo && (phone || email) && (
                  <span className="opacity-60 font-normal">•</span>
                )}
                {phone && (
                  <span className="inline-flex items-center gap-0.5">
                    <span>الهاتف:</span>
                    <span dir="ltr">{phone}</span>
                  </span>
                )}
                {phone && email && (
                  <span className="opacity-60 font-normal">•</span>
                )}
                {email && (
                  <span className="inline-flex items-center gap-0.5">
                    <span>البريد:</span>
                    <span dir="ltr">{email}</span>
                  </span>
                )}
              </div>
            </div>
          )}
          <div className="relative  mb-2 mt-2 flex items-end">
            {!showBusinessLogo && (
              <div
                className="chai-header-divider-line w-6 h-[3px] bg-[#D6515E]"
                style={{
                  backgroundColor: "#D6515E",
                  height: "3px",
                  WebkitPrintColorAdjust: "exact",
                  printColorAdjust: "exact",
                }}
              />
            )}
            <div
              className="chai-header-title-badge relative z-10 inline-flex items-center rounded-t-2xl rounded-b-none bg-[#D6515E] px-4 py-1.5 !text-white"
              style={{
                backgroundColor: "#D6515E",
                color: "#ffffff",
                WebkitTextFillColor: "#ffffff",
                WebkitPrintColorAdjust: "exact",
                printColorAdjust: "exact",
              }}
            >
              <span
                className="text-sm font-bold uppercase tracking-wider"
                style={{ color: "#ffffff", WebkitTextFillColor: "#ffffff" }}
              >
                {activeTitle}
              </span>
            </div>

            <div
              className="chai-header-divider-line flex-1 h-[3px] bg-[#D6515E]"
              style={{
                backgroundColor: "#D6515E",
                height: "3px",
                WebkitPrintColorAdjust: "exact",
                printColorAdjust: "exact",
              }}
            />
          </div>
        </div>
      </div>

      {subtitle && (
        <div className="mt-1 text-center">
          <h3 className="text-xs font-bold tracking-wider text-gray-700 uppercase">
            {subtitle}
          </h3>
        </div>
      )}
    </header>
  );
}
